import React, { useState, useEffect } from 'react';
import { Bug, X, Copy, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { toast } from 'sonner';

function DebugPanel({ user }) {
  const [isOpen, setIsOpen] = useState(false);
  const [errors, setErrors] = useState([]);
  const [expandedIndex, setExpandedIndex] = useState(null);

  // Listen for error events from axios interceptor
  useEffect(() => {
    const handleError = (event) => {
      if (event.detail) {
        setErrors((prev) => [event.detail, ...prev].slice(0, 50)); // Keep last 50 errors
      }
    };

    window.addEventListener('api-error', handleError);
    return () => window.removeEventListener('api-error', handleError);
  }, []);

  // Only show for admins
  if (!user || user.role !== 'admin') {
    return null;
  }

  const handleClearErrors = () => {
    setErrors([]);
    toast.success('Error log cleared');
  };

  const handleCopyError = (error) => {
    const errorText = JSON.stringify(error, null, 2);
    navigator.clipboard.writeText(errorText);
    toast.success('Error details copied to clipboard');
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const getStatusColor = (status) => {
    if (status >= 500) return 'text-red-600 bg-red-50';
    if (status >= 400) return 'text-orange-600 bg-orange-50';
    return 'text-gray-600 bg-gray-50';
  };

  return (
    <>
      {/* Floating Debug Icon */}
      <div className="fixed bottom-4 left-4 z-50">
        <Button
          onClick={() => setIsOpen(!isOpen)}
          className="rounded-full w-12 h-12 shadow-lg bg-purple-600 hover:bg-purple-700 relative"
          title="Admin Debug Panel"
        >
          <Bug className="w-5 h-5" />
          {errors.length > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
              {errors.length > 9 ? '9+' : errors.length}
            </span>
          )}
        </Button>
      </div>

      {/* Debug Panel */}
      {isOpen && (
        <Card className="fixed bottom-20 left-4 z-50 w-96 max-h-[600px] shadow-2xl flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b bg-purple-50">
            <div className="flex items-center gap-2">
              <Bug className="w-5 h-5 text-purple-600" />
              <h3 className="font-semibold text-purple-900">Debug Panel</h3>
              <span className="text-xs text-purple-600 bg-purple-100 px-2 py-1 rounded-full">
                {errors.length} errors
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="ghost"
                onClick={handleClearErrors}
                disabled={errors.length === 0}
                title="Clear all errors"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setIsOpen(false)}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Error List */}
          <div className="flex-1 overflow-y-auto p-4 space-y-2">
            {errors.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Bug className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p className="text-sm">No errors logged yet</p>
                <p className="text-xs mt-1">API errors will appear here</p>
              </div>
            ) : (
              errors.map((error, index) => (
                <Card key={index} className="p-3 space-y-2">
                  {/* Error Summary */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs px-2 py-0.5 rounded font-medium ${getStatusColor(error.status)}`}>
                          {error.status || 'ERR'}
                        </span>
                        <span className="text-xs font-medium text-gray-600">
                          {error.method}
                        </span>
                      </div>
                      <p className="text-xs text-gray-600 truncate" title={error.url}>
                        {error.url}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {formatTimestamp(error.timestamp)}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 p-0"
                        onClick={() => handleCopyError(error)}
                        title="Copy error details"
                      >
                        <Copy className="w-3 h-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 w-7 p-0"
                        onClick={() => setExpandedIndex(expandedIndex === index ? null : index)}
                        title="Toggle details"
                      >
                        {expandedIndex === index ? (
                          <ChevronUp className="w-3 h-3" />
                        ) : (
                          <ChevronDown className="w-3 h-3" />
                        )}
                      </Button>
                    </div>
                  </div>

                  {/* Error Details */}
                  {expandedIndex === index && (
                    <div className="space-y-2 pt-2 border-t">
                      {error.message && (
                        <div>
                          <p className="text-xs font-medium text-gray-700 mb-1">Message:</p>
                          <p className="text-xs text-red-600 bg-red-50 p-2 rounded">
                            {error.message}
                          </p>
                        </div>
                      )}
                      
                      {error.data && (
                        <div>
                          <p className="text-xs font-medium text-gray-700 mb-1">Response Data:</p>
                          <pre className="text-xs bg-gray-50 p-2 rounded overflow-x-auto max-h-40">
                            {JSON.stringify(error.data, null, 2)}
                          </pre>
                        </div>
                      )}

                      {error.requestData && (
                        <div>
                          <p className="text-xs font-medium text-gray-700 mb-1">Request Data:</p>
                          <pre className="text-xs bg-gray-50 p-2 rounded overflow-x-auto max-h-40">
                            {JSON.stringify(error.requestData, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  )}
                </Card>
              ))
            )}
          </div>
        </Card>
      )}
    </>
  );
}

export default DebugPanel;
